// ./src/components/features/CreateEventWizard/useWizardExitGuard.ts
//
// Stops the user from ACCIDENTALLY leaving the wizard.
//
// Two escape routes are guarded:
//   1. Back button / mobile edge-swipe → fires `popstate`. We seed a sentinel
//      history entry on mount so the first Back is absorbed; we then re-absorb it
//      and ask the user instead of navigating away.
//   2. Reload / tab-close / hard navigation → `beforeunload`, which shows the
//      browser's native "leave site?" prompt.
//
// NOTE: react-router's `useBlocker` is NOT usable here — this app mounts a plain
// <BrowserRouter> (see @sdflc/react-router-pages), and useBlocker requires a data
// router (RouterProvider) or it throws. The history-trap below is the correct
// approach for a BrowserRouter and has the bonus of catching edge-swipe on mobile.

import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export interface UseWizardExitGuardParams {
  /** Guard is active only while true (e.g. wizard mounted and not yet finished). */
  enabled: boolean;
  /** Called when a Back/swipe is intercepted — the shell opens its confirm modal. */
  onIntercept: () => void;
  /** Where "Leave" sends the user (default resolved by the caller). */
  destination: string;
}

export interface UseWizardExitGuardReturn {
  /** Confirm-and-leave: disarms the guard and navigates to `destination`. */
  leaveNow: () => void;
}

export const useWizardExitGuard = ({
  enabled,
  onIntercept,
  destination,
}: UseWizardExitGuardParams): UseWizardExitGuardReturn => {
  const navigate = useNavigate();
  // Once the user confirms leaving we must stop intercepting, or the programmatic
  // navigation itself could be swallowed.
  const bypassRef = useRef(false);
  // Keep the latest onIntercept without re-arming the listeners each render.
  const onInterceptRef = useRef(onIntercept);
  onInterceptRef.current = onIntercept;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    bypassRef.current = false;

    // Seed a sentinel entry: the first Back lands here (same URL) instead of
    // leaving, and popstate lets us intercept it.
    window.history.pushState(null, document.title, window.location.href);

    const handlePopState = () => {
      if (bypassRef.current) {
        return;
      }
      // Re-absorb the Back so we stay put, then ask.
      window.history.pushState(null, document.title, window.location.href);
      onInterceptRef.current();
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (bypassRef.current) {
        return;
      }
      event.preventDefault();
      // Required by some browsers to trigger the native prompt.
      event.returnValue = '';
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled]);

  const leaveNow = useCallback(() => {
    bypassRef.current = true;
    navigate(destination);
  }, [navigate, destination]);

  return { leaveNow };
};
